import React from "react";
import { TenantSelectDropdown } from "../../molecules/TenantSelectDropdown";
import { RetryButton as BackLink } from "../../molecules/TenantSelectList/TenantSelectList.styles";
import { useTenantMemberships } from "../../../hooks/useTenantContext";
import { getApiErrorMessage } from "../../../services/apiErrors";
import {
  AuthFormWrapper,
  AuthFieldGroup,
  AuthButton,
  AuthErrorMsg,
} from "../../../styles/authForm.styles";
import type { TenantSelectFormProps } from "./TenantSelectForm.types";
import { useTenantSelectInternalState } from "./useTenantSelectInternalState";

/**
 * The "Select tenant" step — rendered inside the switch-organization dialog.
 *
 * Two calls happen across this step:
 *   1. the tenant memberships, via `useTenantMemberships`;
 *   2. the chosen tenant's workspaces, inside `handleContinue`.
 *
 * A compositor only — selection and the continue call live in
 * `useTenantSelectInternalState`.
 */
export const TenantSelectForm: React.FC<TenantSelectFormProps> = ({
  onContinue,
  onUnauthorized,
  onBack,
  backLabel = "Back to login",
  continueLabel = "Continue to home",
  initialTenantId = null,
  autoContinueSingleTenant = false,
  className,
}) => {
  const {
    data: tenants,
    isPending,
    isError,
    error,
    refetch,
  } = useTenantMemberships();
  const {
    selectedTenantId,
    selectTenant,
    canContinue,
    isContinuing,
    continueError,
    handleContinue,
  } = useTenantSelectInternalState({
    tenants: tenants ?? [],
    initialTenantId,
    onContinue,
    onUnauthorized,
    autoContinueSingleTenant,
  });

  return (
    <AuthFormWrapper className={className}>
      <AuthFieldGroup>
        <TenantSelectDropdown
          tenants={tenants ?? []}
          selectedTenantId={selectedTenantId}
          onSelect={selectTenant}
          isLoading={isPending}
          errorMessage={
            isError
              ? getApiErrorMessage(error, "Couldn't load your organizations.")
              : null
          }
          onRetry={refetch}
          disabled={isContinuing}
        />

        {continueError && <AuthErrorMsg role="alert">{continueError}</AuthErrorMsg>}

        <AuthButton
          variant="primary"
          size="sm"
          fullWidth
          onClick={handleContinue}
          disabled={!canContinue}
          loading={isContinuing}
        >
          {continueLabel}
        </AuthButton>

        {onBack && <BackLink onClick={onBack}>{backLabel}</BackLink>}
      </AuthFieldGroup>
    </AuthFormWrapper>
  );
};

export default TenantSelectForm;
